import { Avatar, Box, Stack, Tooltip, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { ReviewChangeRequest } from "../../types";
import { reviewTokens } from "../../theme";
import { CrSubmitter, CrSubmitterResolver } from "./useCrSubmitter";

interface Props {
  cr: ReviewChangeRequest;
  resolve: CrSubmitterResolver;
  size?: "sm" | "md";
}

interface KindStyle {
  bg: string;
  fg: string;
  ring: string;
  Icon: typeof ShieldOutlinedIcon;
}

const KIND_STYLES: Record<CrSubmitter["kind"], KindStyle> = {
  admin: {
    bg: "#EEE7FF",
    fg: "#6D28D9",
    ring: "#C4B5FD",
    Icon: ShieldOutlinedIcon,
  },
  audience: {
    bg: reviewTokens.brandSoft,
    fg: reviewTokens.brandHover,
    ring: "#FBC990",
    Icon: PersonOutlineOutlinedIcon,
  },
  anonymous: {
    bg: "#FFEDD5",
    fg: "#C2410C",
    ring: "#FDBA74",
    Icon: VisibilityOutlinedIcon,
  },
  unknown: {
    bg: reviewTokens.slate100,
    fg: reviewTokens.slate600,
    ring: reviewTokens.slate300,
    Icon: PersonOutlineOutlinedIcon,
  },
};

export function SubmitterPill({ cr, resolve, size = "sm" }: Props) {
  const submitter = resolve(cr);
  const style = KIND_STYLES[submitter.kind];
  const isMd = size === "md";
  const avatarSize = isMd ? 22 : 18;
  const fontSize = isMd ? 12 : 11;
  const IconComponent = style.Icon;

  return (
    <Tooltip
      title={
        submitter.audienceKey
          ? `${submitter.name} · ${submitter.audienceKey}`
          : submitter.name
      }
      placement="top"
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        component="span"
        data-testid={`cr-submitter-${submitter.kind}-${cr.id}`}
        sx={{
          display: "inline-flex",
          px: 0.625,
          py: 0.125,
          borderRadius: `${reviewTokens.radiusSm}px`,
          bgcolor: style.bg,
          border: `1px solid ${style.ring}`,
          maxWidth: isMd ? 220 : 180,
          overflow: "hidden",
        }}
      >
        <Avatar
          sx={{
            width: avatarSize,
            height: avatarSize,
            bgcolor: style.fg,
            color: "#fff",
            fontSize: avatarSize === 22 ? 10 : 9,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {submitter.kind === "anonymous" || submitter.kind === "unknown" ? (
            <IconComponent sx={{ fontSize: avatarSize === 22 ? 13 : 11 }} />
          ) : (
            submitter.initials
          )}
        </Avatar>
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center" }}>
          <Typography
            component="span"
            sx={{
              fontSize,
              fontWeight: 600,
              color: style.fg,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {submitter.name}
          </Typography>
        </Box>
      </Stack>
    </Tooltip>
  );
}
